import { APIService } from './api/api.service';
import { DOCUMENT, Inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { HttpClient, HttpContext } from '@angular/common/http';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {
  authenticated,
  clearToken,
  logoutOAuth,
  restoreSession,
  secondFactorEnforced,
  secondFactorFailed,
} from '../store/actions/session-auth.actions';
import { tokenSelector } from '../store/selectors/session.selectors';
import { selectBffBaseUrl } from '../store/selectors/api-config.selectors';
import { RedirectService } from './redirect.service';
import { NativeClientService } from '@shared/services/native-client.service';
import { OAuthLoginResponse } from '../interfaces/oauth-login.interface';
import { AuthenticationNotificationProvider } from '@xt/data-access';
import { MessengerService } from './messenger/messenger.service';
import { userRoleNewSelector } from '@core/store/selectors/user-role.selectors';
import { SKIP_ERROR_HANDLER } from '@core/interceptors/skip-error-handler';
import { fullyDecodeUri, nonNullable } from '@xt/core/utils';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements AuthenticationNotificationProvider {
  // session token
  private loginRequest?: Observable<OAuthLoginResponse>;
  private bffBaseUrl$: Observable<string>;

  constructor(
    private apiService: APIService,
    private store: Store,
    private router: Router,
    private http: HttpClient,
    private redirectService: RedirectService,
    private nativeClientService: NativeClientService,
    private messengerService: MessengerService,
    @Inject(DOCUMENT) private browserDocument: Document,
  ) {
    this.bffBaseUrl$ = this.store.pipe(selectBffBaseUrl);
  }

  /**
   * Connects to the oAuth server.
   *
   * @param isAutoLogin boolean, to check if the user can log in automatically.
   * @param url page to redirect to
   * @param params are optional parameters that are added to the login request
   */
  connectOAuth(
    isAutoLogin: boolean = false,
    url: string = '',
    params: { [key: string]: string } = {},
  ): Observable<any> {
    // If no URL is provided, use the current location's path
    const redirect_uri =
      url?.replace(/^\/*/g, '') || this.getCurrentRelativePath();

    const redirectParam = `redirect_uri=${encodeURIComponent(
      `${this.browserDocument.location.origin}/${fullyDecodeUri(redirect_uri)}`,
    )}`;

    const paramsString: string = [
      redirectParam,
      ...Object.entries(params)
        .filter((pair) => pair.every(nonNullable))
        .map((pair) => pair.join('=')),
    ].join('&');

    this.loginRequest = this.apiService
      .callGetAPI<OAuthLoginResponse>(
        `/login${isAutoLogin ? '' : '?' + paramsString}`,
      )
      .pipe(shareReplay(1));

    this.loginRequest.subscribe((response: OAuthLoginResponse) => {
      const token = response?.X_CSRF_TOKEN;
      if (token) {
        this.store.dispatch(authenticated({ token }));

        if (redirect_uri) {
          const url = fullyDecodeUri(redirect_uri);
          this.router.navigateByUrl(url);
        }
      } else {
        this.store.dispatch(clearToken());
        if (!isAutoLogin) {
          this.redirectService.redirectTo(response.Location);
        }
      }
    });

    return this.loginRequest;
  }

  private getCurrentRelativePath(): string {
    return this.browserDocument.location.href.replace(
      this.browserDocument.location.origin,
      '',
    );
  }

  /**
   * Get the oAuth API
   *
   * @return response of the oAuth API
   */
  getOAuth() {
    return this.loginRequest;
  }

  public isAuthenticated$(): Observable<boolean> {
    if (this.nativeClientService.isNative) {
      return of(true);
    }
    return (
      this.loginRequest?.pipe(map((response) => !!response.X_CSRF_TOKEN)) ||
      of(false)
    );
  }

  logoutOAuth() {
    this.loginRequest = null;
    this.store.dispatch(logoutOAuth());

    // logout messenger in parallel so it doesn't block the logout process
    this.messengerService.logout().pipe(take(1)).subscribe();
    this.bffBaseUrl$.subscribe((bffBaseUrl) => {
      this.redirectService.redirectTo(`${bffBaseUrl}/logout`);
    });
  }

  hiddenLogout() {
    this.loginRequest = null;
    this.store.dispatch(clearToken());

    return this.bffBaseUrl$.pipe(
      switchMap((bffBaseUrl) => this.http.get(`${bffBaseUrl}/hiddenLogout`)),
    );
  }

  relogin(): Observable<any> {
    return this.hiddenLogout().pipe(
      tap(() =>
        this.connectOAuth(
          false,
          this.browserDocument.location.href.replace(
            this.browserDocument.location.origin,
            '',
          ),
        ),
      ),
    );
  }

  /**
   * Gets the token
   */
  getToken(): string | null {
    let token;
    this.store.select(tokenSelector).subscribe((t) => {
      token = t;
    });
    if (token == null) {
      token = this.getCsrfToken();
    }

    return token;
  }

  getCsrfToken(): string | null {
    return window.sessionStorage.getItem('X_CSRF_TOKEN');
  }

  /**
   * Reconstructs loginRequest from the CSRF token stored in sessionStorage
   * without making a new BFF call. Used on page refresh so the guard can pass
   * without triggering a full OAuth flow (which prompts for 2FA on
   * credential-only sessions).
   */
  public initFromSessionStorage(): void {
    if (this.loginRequest) return;
    const token = this.getCsrfToken();
    if (token) {
      this.loginRequest = of({
        X_CSRF_TOKEN: token,
      }).pipe(shareReplay(1));
      this.store.dispatch(restoreSession({ token }));
    }
  }

  /**
   * Enforces 2FA silently without throwing errors.
   * Use this for fire-and-forget scenarios where you don't need to handle errors.
   *
   * @param redirectUrl Optional URL to redirect to after 2FA enforcement
   * @returns Observable that completes silently on error
   */
  public enforce2FASilent$(redirectUrl?: string): Observable<unknown> {
    return this.enforce2FA$(redirectUrl, true).pipe(catchError(() => EMPTY));
  }

  public enforce2FA$(
    redirectUrl?: string,
    skipError: boolean = false,
  ): Observable<unknown> {
    if (this.nativeClientService.isNative) {
      return this.store.select(userRoleNewSelector).pipe(
        take(1),
        switchMap((roles) =>
          this.nativeClientService.assertUserRolesScopes(roles, redirectUrl),
        ),
      );
    } else {
      return this.bffBaseUrl$.pipe(
        switchMap((url) => {
          return this.apiService
            .callGetAPI(
              `${url}/enforce-2fa`,
              undefined,
              undefined,
              undefined,
              skipError
                ? new HttpContext().set(SKIP_ERROR_HANDLER, true)
                : undefined,
            )
            .pipe(
              // if no error occurs, second factor was enforced
              tap(() => this.store.dispatch(secondFactorEnforced())),
              catchError((error) => {
                this.store.dispatch(secondFactorFailed());

                if (error?.error?.useBffLogin === true) {
                  if (!skipError) {
                    this.connectOAuth(false, redirectUrl);
                  } else {
                    return EMPTY;
                  }
                }

                return throwError(() => error);
              }),
            );
        }),
      );
    }
  }
}
